import { filter, maxBy } from 'lodash';
import { createSelector } from 'reselect';

import { dataSources } from 'helpers/constants';
import { getTimeSinceDateString } from 'helpers/dateHelper';
import {
    getFlatAvailableKPIList,
    //formatChartData,
    formatChartDataV2
} from '../../../../commonTrendHelpers';
import { getOpTrendUniqueYScaling } from '../helpers';
import { getAssetDataSourceType } from 'helpers/assetAndPowertrainFilterHelper';
import { getTrendKPIsList } from '../TrendKPIList/selectors';
import { energyViewModes, STACKED_AREA_CHART } from '../EnergyRelatedSignalsList/constants';
import { TIME_SERIES_CATEGORY } from '../../../CommonCharts/Trend/constants';
import UserCapabilityHelper from 'helpers/userCapabilityHelper';

const getSelectedTrendKPIs = ({ operationalParameters }) => {
    return operationalParameters?.preselectedKPIs?.single?.trend;
};

const getAvailableTrendKPIs = ({ operationalParameters }) => {
    return operationalParameters?.availableKPIs?.trend?.data;
};

const getUserLimits = ({ detailedInformationHeader }) => {
    return detailedInformationHeader?.userLimits?.data;
};

const getTrendHealthStatuses = ({ operationalParameters }) => {
    return operationalParameters?.trendHealthStatuses?.data;
};

const getEnableUniqueScaling = ({ operationalParameters }) => {
    return getOpTrendUniqueYScaling(
        operationalParameters?.charts?.single?.trend[0]?.kpiList,
        operationalParameters?.availableKPIs?.trend?.data
    );
};

const getTrendData = ({ operationalParameters }) => {
    return operationalParameters?.charts?.single?.trend;
};

const getDateSelection = ({ overview }) => {
    return overview?.dateSelection;
};

const getUserCapabilities = ({ overview }) => overview?.userCapabilities;

const getIsOperationalParametersEnergyCapabilityEnabled = createSelector([
    getUserCapabilities
], (capability) => {
    const userCapabilityHelper = new UserCapabilityHelper(capability);
    return userCapabilityHelper.hasOperationalParametersEnergyCapability();
});

export const getTrendDataSelector = createSelector([
    getAvailableTrendKPIs,
    getTrendData
],
    (
        availableTrendKpis,
        data2
    ) => {

        const data = formatChartDataV2(
            data2 || [],
            availableTrendKpis
        );
        return data[0];
    });

const getLastMeasurementTimeStampLabel = createSelector([
    getAvailableTrendKPIs,
    ({ powertrainAssetDetails }) => powertrainAssetDetails.selectedAsset
],
    (trendKPIsData,
        asset
    ) => {
        let label = null;
        if (getAssetDataSourceType(asset) === dataSources.SMARTSENSOR) {
            const availableTrendKPIList = getFlatAvailableKPIList(trendKPIsData);
            const mType = maxBy(Object.values(availableTrendKPIList),
                (measurementType) => {
                    return measurementType.timeseriesLastValueTimeStamp;
                });
            label = getTimeSinceDateString(mType?.timeseriesLastValueTimeStamp);
        }
        return label;
    });

const getUserLimitsSelector = createSelector([
    getUserLimits,
    ({ operationalParameters }) => operationalParameters?.charts?.single?.trend[0]?.kpiList,
    getAvailableTrendKPIs,
],
    (userLimits,
        selectedTrendsData,
        trendKPIsData
    ) => {
        let kpiUserLimits = [];
        const availableTrendKPIList = getFlatAvailableKPIList(trendKPIsData);
        if (userLimits && selectedTrendsData.length === 1) {
            const [{ kpiIdentifier, values }] = selectedTrendsData;
            if (values.length) {
                kpiUserLimits = filter(userLimits.visualAids, (limit) => {
                    return limit?.signalCode?.toLowerCase() === availableTrendKPIList[kpiIdentifier]?.timeseriesKey?.toLowerCase();
                });
            }
        }
        return kpiUserLimits;
    });

const getAssetDetails = ({ powertrainAssetDetails }) => {
    return powertrainAssetDetails.selectedAsset;
};

const getSelectedLimits = ({ operationalParameters }) => {
    return operationalParameters?.selectedLimits;
};

export const getMaintainSelectedLimits = ({ operationalParameters }) => {
    return operationalParameters?.maintainSelectedLimits;
};

export const getSelectedOrganization = ({ generalFilter }) => {
    return generalFilter?.selectedFilters?.organizations[0];
};

const getCommentsList = ({ operationalParameters }) => {
    return operationalParameters?.trendCommentsList?.data;
};

const getEnergyViewMode = ({ operationalParameters }) => {
    return operationalParameters?.energyViewMode || energyViewModes.OVERALL;
};

// Helper to identify energy-related signals
const getEnergyRelatedKPIsList = createSelector([
    getAvailableTrendKPIs,
    getIsOperationalParametersEnergyCapabilityEnabled
], (trendKPIs, isOperationalParametersEnergyCapabilityEnabled) => {
    if (!isOperationalParametersEnergyCapabilityEnabled) {
        return [];
    }

    const energyRelatedSignals = [];
    trendKPIs?.forEach((kpi) => {
        const kpiObj = kpi.data;
        const keys = Object.keys(kpiObj);
        keys?.forEach(key => {
            const newObj = kpiObj[key];
            // Filter for energy-related signals based on timeseriesCategory field from API
            // Fallback to legacy naming convention if timeseriesCategory is not available
            const isEnergyByCategory = newObj.timeseriesCategory === TIME_SERIES_CATEGORY.ENERGY;
            if (isEnergyByCategory) {
                energyRelatedSignals.push(newObj);
            }
        });
    });
    return energyRelatedSignals;
});

// Check if only energy-related signals are selected
const getIsOnlyEnergySignalsSelected = createSelector([
    getSelectedTrendKPIs,
    getEnergyRelatedKPIsList,
    getTrendDataSelector
], (selectedKPIs, energyKPIs, trendData) => {
    if (!selectedKPIs?.length) {
        return false;
    }

    // Check if any selected signal has StackedAreaChart visualization
    // trendData.data is the formatted array from formatChartDataV2
    const hasStackedAreaChart = trendData?.data?.some(
        item => item?.config?.details?.visualization === STACKED_AREA_CHART
    );

    if (hasStackedAreaChart) {
        return true;
    }

    const energyKPIKeys = new Set(energyKPIs.map(kpi => kpi.timeseriesKey));
    return selectedKPIs.every(selectedKPI => energyKPIKeys.has(selectedKPI));
});

// Check if there's a mixed selection of energy and non-energy signals
const getHasMixedSignalSelection = createSelector([
    getSelectedTrendKPIs,
    getEnergyRelatedKPIsList
], (selectedKPIs, energyKPIs) => {
    if (!selectedKPIs?.length) {
        return false;
    }

    const energyKPIKeys = new Set(energyKPIs.map(kpi => kpi.timeseriesKey));
    const hasEnergySignals = selectedKPIs.some(selectedKPI => energyKPIKeys.has(selectedKPI));
    const hasNonEnergySignals = selectedKPIs.some(selectedKPI => !energyKPIKeys.has(selectedKPI));

    return hasEnergySignals && hasNonEnergySignals;
});

const getSelectedDataPointTimestamp = ({ operationalParameters }) => {
    return operationalParameters?.selectedDataPointTimestamp;
};

export const getModel = createSelector([
    getSelectedTrendKPIs,
    getTrendDataSelector,
    getUserLimitsSelector,
    getEnableUniqueScaling,
    getLastMeasurementTimeStampLabel,
    getTrendHealthStatuses,
    getAvailableTrendKPIs,
    getDateSelection,
    getAssetDetails,
    getSelectedLimits,
    getMaintainSelectedLimits,
    getTrendKPIsList,
    getSelectedOrganization,
    getCommentsList,
    getEnergyViewMode,
    getIsOnlyEnergySignalsSelected,
    getHasMixedSignalSelection,
    getSelectedDataPointTimestamp
], (
    selectedTrendKPIs,
    selectedTrendsData,
    userLimits,
    enableUniqueScaling,
    lastMeasurementTimeStampLabel,
    trendHealthStatuses,
    availableTrendKPIs,
    dateSelection,
    selectedAsset,
    selectedLimits,
    maintainSelectedLimits,
    trendKPIsList,
    selectedOrganization,
    trendComments,
    energyViewMode,
    isOnlyEnergySignalsSelected,
    hasMixedSignalSelection,
    selectedDataPointTimestamp
) => {
    return {
        selectedTrendKPIs,
        selectedTrendsData,
        userLimits,
        enableUniqueScaling,
        lastMeasurementTimeStampLabel,
        trendHealthStatuses,
        availableTrendKPIs,
        dateSelection,
        selectedAsset,
        selectedLimits,
        maintainSelectedLimits,
        trendKPIsList,
        selectedOrganization,
        trendComments,
        energyViewMode,
        isOnlyEnergySignalsSelected,
        hasMixedSignalSelection,
        selectedDataPointTimestamp
    };
});
